import { createSlice } from "@reduxjs/toolkit";
import { getVisits, getMyVisits, getVisitById, addVisit, updateVisit, deleteVisit } from "./thunk";

// Define visit interface
interface Visit {
    id: string;
    visit_code: string;
    company_id?: string;
    company_name?: string;
    visited_by_id?: string;
    visited_by_fullname?: string;
    phone?: string;
    email?: string;
    source?: string;
    status?: string;
    notes?: string;
    visited_on?: string;
    next_visit_date?: string;
    next_visit_purpose?: string;
    next_visit_notes?: string;
}

const initialState = {
    visits: [] as Visit[],
    myVisits: [] as Visit[],
    selectedVisit: null as Visit | null,
    loading: false,
    error: null as string | null
};

const visitSlice = createSlice({
    name: "visits",
    initialState,
    reducers: {
        clearSelectedVisit: (state) => {
            state.selectedVisit = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Get all visits
        builder.addCase(getVisits.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getVisits.fulfilled, (state, action) => {
            state.loading = false;
            state.visits = action.payload;
        });
        builder.addCase(getVisits.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch visits';
        });

        // Get my visits
        builder.addCase(getMyVisits.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getMyVisits.fulfilled, (state, action) => {
            state.loading = false;
            state.myVisits = action.payload;
        });
        builder.addCase(getMyVisits.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch my visits';
        });

        // Get visit by ID
        builder.addCase(getVisitById.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getVisitById.fulfilled, (state, action) => {
            state.loading = false;
            state.selectedVisit = action.payload;
        });
        builder.addCase(getVisitById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch visit';
        });

        // Add visit
        builder.addCase(addVisit.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addVisit.fulfilled, (state, action) => {
            state.loading = false;
            state.visits.push(action.payload);
        });
        builder.addCase(addVisit.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to add visit';
        });

        // Update visit
        builder.addCase(updateVisit.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateVisit.fulfilled, (state, action) => {
            state.loading = false;
            const index = state.visits.findIndex(visit => visit.id === action.payload.id);
            if (index !== -1) {
                state.visits[index] = action.payload;
            }
        });
        builder.addCase(updateVisit.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to update visit';
        });

        // Delete visit
        builder.addCase(deleteVisit.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteVisit.fulfilled, (state, action) => {
            state.loading = false;
            state.visits = state.visits.filter(visit => visit.id !== action.payload);
        });
        builder.addCase(deleteVisit.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to delete visit';
        });
    }
});

export const { clearSelectedVisit, clearError } = visitSlice.actions;
export default visitSlice.reducer;
